/**
 * Despia native biometric helper.
 *
 * Inside Despia's iOS WKWebView (and the Android counterpart), the standard
 * Web platform `navigator.credentials.create()` (WebAuthn) is not exposed —
 * iOS WKWebView in third-party apps does not surface Face ID/Touch ID to JS.
 *
 * Despia ships a native bridge: `despia('bioauth://')` triggers the OS
 * biometric prompt and resolves via globally-named callbacks. This helper
 * wraps the callback-based bridge into a Promise the rest of the app can use
 * just like a WebAuthn call.
 *
 * The bridge is just a "user is physically here right now" signal — there is
 * no per-credential public key. To still get cross-device per-account
 * security we pair it with a server-stored shared secret: the device
 * generates a 256-bit secret on enrollment, the server stores its hash, and
 * on each unlock the device re-sends the secret AFTER bioauth succeeds.
 */

export function isDespiaRuntime() {
  if (typeof navigator === 'undefined') return false;
  const ua = navigator.userAgent.toLowerCase();
  if (ua.includes('despia')) return true;
  // Fallback detection: Despia injects `window.despia` as a setter on the
  // native side. Some Despia builds (especially custom-branded ones) ship a
  // user-agent that doesn't include the "despia" token, so checking the
  // injected global catches those cases.
  if (typeof window !== 'undefined' && typeof window.despia !== 'undefined') {
    // But window.despia is also created by the npm `despia-native` package
    // (a Proxy), so we must confirm we're inside a wrapped native shell —
    // check for the absence of a writable Window despia setter via touch
    // of a native handler hint.
    if (typeof window.webkit !== 'undefined' && window.webkit?.messageHandlers) return true;
    if (typeof window.Android !== 'undefined') return true;
  }
  return false;
}

/**
 * Detect iOS-specific Despia runtime.
 *
 * iOS WKWebView in third-party apps does NOT expose WebAuthn
 * (`navigator.credentials`) to JavaScript — Apple's restriction. So
 * iOS Despia *must* use the native `despia(...)` bridge to surface
 * Face ID / Touch ID.
 *
 * Android Chrome WebView (which Despia ships) DOES expose WebAuthn
 * natively (API 28+, Android 9+). Despia Android does not implement a
 * standalone `bioauth://` URL scheme — its biometric support is
 * delivered through the Identity Vault `readvault://?locked=true` flow
 * only. Calling `bioauth://` on Android Despia therefore returns
 * "unavailable" (or never callbacks), which is why the previous code
 * showed "Biometric is not available on this device" even when the
 * device clearly had fingerprint / face unlock configured.
 *
 * Use this helper instead of `isDespiaRuntime()` when gating the
 * Despia bioauth bridge specifically.
 */
export function isIosDespiaRuntime() {
  if (!isDespiaRuntime()) return false;
  const ua = navigator.userAgent.toLowerCase();
  return ua.includes('iphone') || ua.includes('ipad') || ua.includes('ipod');
}

const SDK_SCRIPT_ID = 'eili-bioauth-sdk';

/**
 * Pre-load the `despia-native` ESM chunk so that the click handler that calls
 * `triggerDespiaBioauth` doesn't have to await a network/chunk fetch — that
 * await would consume the browser's transient user activation token and the
 * OS biometric prompt would never appear.
 *
 * Safe to call unconditionally on app mount; resolves immediately on web.
 */
let _despiaModulePromise = null;
export function warmupDespiaBridge() {
  if (typeof window === 'undefined') return;
  if (!_despiaModulePromise) {
    _despiaModulePromise = import('despia-native').catch((e) => {
      console.warn('[bioauth] warmup load failed (will retry on demand):', e);
      _despiaModulePromise = null;
    });
  }
  // Also pre-inject the SDK bridge globals
  try { ensureBioauthSdk(); } catch {}
}

function ensureBioauthSdk() {
  if (typeof document === 'undefined') return;
  if (document.getElementById(SDK_SCRIPT_ID)) return;
  const script = document.createElement('script');
  script.id = SDK_SCRIPT_ID;
  script.type = 'text/javascript';
  // Despia's required bridge functions — they call back into our window globals
  script.textContent = `
    function onBioAuthSuccess() {
      if (window.__eiliBioauthSuccess) window.__eiliBioauthSuccess();
    }
    function onBioAuthFailure(errorCode, errorMessage) {
      if (window.__eiliBioauthFailure) window.__eiliBioauthFailure(errorCode, errorMessage);
    }
    function onBioAuthUnavailable() {
      if (window.__eiliBioauthUnavailable) window.__eiliBioauthUnavailable();
    }
  `;
  document.head.appendChild(script);
}

/**
 * Trigger the native Despia biometric prompt.
 * Resolves to true on success, throws Error('cancelled'|'unavailable'|...) otherwise.
 *
 * IMPORTANT: We synchronously eager-import `despia-native` (top of file) so
 * that calling this from inside a click handler doesn't break the browser's
 * "transient user activation" — the user-gesture token Apple/Chrome require
 * for surfacing biometric UI. A dynamic `import()` would add a chunk-load
 * await that silently invalidates the gesture and the OS prompt never fires.
 */
export async function triggerDespiaBioauth(reason = 'Unlock vault') {
  if (!isDespiaRuntime()) throw new Error('not_despia');
  ensureBioauthSdk();

  let despia;
  try {
    // Prefer the cached warmup promise so we don't add chunk-load latency
    // inside the user-activation window. Fall back to a fresh dynamic import
    // if warmup was never called (defensive).
    if (!_despiaModulePromise) warmupDespiaBridge();
    const mod = await (_despiaModulePromise || import('despia-native'));
    despia = mod?.default || mod;
    if (typeof despia !== 'function') {
      throw new Error('despia export not a function');
    }
  } catch (e) {
    console.error('[bioauth] despia-native module failed to load:', e);
    throw new Error('bridge_load_failed');
  }

  return await new Promise((resolve, reject) => {
    let settled = false;
    const cleanup = () => {
      delete window.__eiliBioauthSuccess;
      delete window.__eiliBioauthFailure;
      delete window.__eiliBioauthUnavailable;
    };
    const finish = (fn, val) => {
      if (settled) return;
      settled = true;
      cleanup();
      fn(val);
    };

    window.__eiliBioauthSuccess = () => {
      console.log('[bioauth] native success callback received');
      finish(resolve, true);
    };
    window.__eiliBioauthFailure = (code, msg) => {
      console.warn('[bioauth] native failure callback:', code, msg);
      finish(reject, new Error(`bioauth_failed:${code}:${msg}`));
    };
    window.__eiliBioauthUnavailable = () => {
      console.warn('[bioauth] native unavailable callback');
      finish(reject, new Error('unavailable'));
    };

    // Hard timeout so we don't leave promises hanging if the bridge never calls back
    setTimeout(() => {
      if (!settled) console.warn('[bioauth] timeout waiting for native callback (60s)');
      finish(reject, new Error('timeout'));
    }, 60_000);

    try {
      console.log('[bioauth] dispatching bioauth:// to native bridge');
      despia(`bioauth://?reason=${encodeURIComponent(reason)}`);
    } catch (e) {
      console.error('[bioauth] bridge dispatch threw:', e);
      finish(reject, new Error(`bridge_error:${String(e)}`));
    }
  });
}

/** Generate a cryptographically-strong per-device secret. */
export function generateDeviceSecret() {
  const bytes = new Uint8Array(32);
  crypto.getRandomValues(bytes);
  return Array.from(bytes).map((b) => b.toString(16).padStart(2, '0')).join('');
}

const SECRET_LS_KEY = (userId) => `vault_bioauth_secret_${userId || 'anon'}`;

export function saveDeviceSecret(userId, secret) {
  try {
    localStorage.setItem(SECRET_LS_KEY(userId), secret);
    return true;
  } catch {
    return false;
  }
}

export function loadDeviceSecret(userId) {
  try {
    return localStorage.getItem(SECRET_LS_KEY(userId));
  } catch {
    return null;
  }
}

export function clearDeviceSecret(userId) {
  try {
    localStorage.removeItem(SECRET_LS_KEY(userId));
  } catch {}
}
